import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonLink } from '../../../components/ButtonLink';
import { StatusPanel } from '../../../components/StatusPanel';
import { apiErrorMessageKey } from '../../../lib/api';
import { invitationInfoQueryOptions, useAcceptInvitationMutation } from '../../../lib/auth';
import { SetPasswordForm } from '../components/SetPasswordForm';
import { TermsConsentForm } from '../components/TermsConsentForm';

function InvitationFailed({ error }: { error: unknown }): ReactElement {
    const { t } = useTranslation();
    return (
        <StatusPanel
            severity="error"
            title={t(apiErrorMessageKey(error, 'ACCEPT_INVITATION.ERRORS.FAILED'))}
            action={
                <ButtonLink variant="contained" color="primary" to="/login">
                    {t('AUTH.BACK_TO_SIGN_IN')}
                </ButtonLink>
            }
        />
    );
}

function SigningIn(): ReactElement {
    const { t } = useTranslation();
    return (
        <Stack spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
                {t('ACCEPT_INVITATION.SIGNING_IN')}
            </Typography>
        </Stack>
    );
}

export function AcceptInvitationPage({ otp }: { otp: string }): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const mutation = useAcceptInvitationMutation();
    const infoQuery = useQuery({ ...invitationInfoQueryOptions(otp), enabled: !mutation.isSuccess });
    const { mutate: acceptInvitation } = mutation;
    const autoConsumed = useRef(false);

    const goHome = useCallback((): void => {
        navigate({ to: '/' }).catch(() => undefined);
    }, [navigate]);

    const requiresPasswordSetup = infoQuery.data?.requiresPasswordSetup === true;
    const requiresTosAcceptance = infoQuery.data?.requiresTosAcceptance === true;
    const needsInput = requiresPasswordSetup || requiresTosAcceptance;

    useEffect(() => {
        if (infoQuery.data && !needsInput && !autoConsumed.current) {
            autoConsumed.current = true;
            acceptInvitation({ otp }, { onSuccess: goHome });
        }
    }, [infoQuery.data, needsInput, acceptInvitation, otp, goHome]);

    if (mutation.isSuccess) {
        return <SigningIn />;
    }

    if (infoQuery.isPending) {
        return (
            <Stack spacing={2} alignItems="center">
                <CircularProgress size={24} />
            </Stack>
        );
    }

    if (infoQuery.isError) {
        return <InvitationFailed error={infoQuery.error} />;
    }

    const errorMessage = mutation.error
        ? t(apiErrorMessageKey(mutation.error, 'ACCEPT_INVITATION.ERRORS.FAILED'))
        : undefined;

    if (!needsInput) {
        if (mutation.isError) {
            return <InvitationFailed error={mutation.error} />;
        }
        return <SigningIn />;
    }

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Typography variant="h5" component="h1">
                    {t(requiresPasswordSetup ? 'ACCEPT_INVITATION.TITLE' : 'AUTH.ACCEPT_TERMS.TITLE')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t(requiresPasswordSetup ? 'ACCEPT_INVITATION.SUBTITLE' : 'AUTH.ACCEPT_TERMS.DESCRIPTION')}
                </Typography>
            </Stack>

            {requiresPasswordSetup ? (
                <SetPasswordForm
                    submitLabel={t('ACCEPT_INVITATION.CONFIRM_BUTTON')}
                    isPending={mutation.isPending}
                    errorMessage={errorMessage}
                    requireTosConsent={requiresTosAcceptance}
                    onSubmit={(password) =>
                        acceptInvitation({ otp, password, tosAccepted: requiresTosAcceptance }, { onSuccess: goHome })
                    }
                />
            ) : (
                <TermsConsentForm
                    submitLabel={t('AUTH.ACCEPT_TERMS.SUBMIT')}
                    isPending={mutation.isPending}
                    errorMessage={errorMessage}
                    onSubmit={() => acceptInvitation({ otp, tosAccepted: true }, { onSuccess: goHome })}
                />
            )}
        </Stack>
    );
}
